import React, { useState, ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// --- IMPORTANT URLS ---
const SPRING_API_URL = 'http://localhost:2027'; // Spring Boot (Products)
const NODE_API_URL = 'http://localhost:2026';   // Node.js (Categories)

interface Category {
  id: number;
  name: string;
  subCategories?: Category[];
}

const AddNewProductPage: React.FC = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form States
    const [productName, setProductName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [minQuantity, setMinQuantity] = useState('');
    const [quantityUnit, setQuantityUnit] = useState('');
    const [hsnSku, setHsnSku] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    
    // Category Logic
    const [selectedCategoryPath, setSelectedCategoryPath] = useState<number[]>([]);
    const [nestedCategories, setNestedCategories] = useState<Category[]>([]);
    
    // Image Logic (Now Array)
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);

    // Status
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // 1. Fetch Categories from Node.js
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                // Node API call for categories
                const response = await fetch(`${NODE_API_URL}/api/categories`);
                if (!response.ok) throw new Error('Failed to fetch categories');
                const data = await response.json();
                setNestedCategories(data);
            } catch (err) {
                console.error(err);
                setError('Error fetching categories. Make sure Node server is running on port 2026.');
            }
        };
        fetchCategories();
    }, []);

    // 2. Handle Multiple Images
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            setSelectedImages(filesArray);

            // Generate previews
            const newPreviews = filesArray.map(file => URL.createObjectURL(file));
            setPreviewUrls(newPreviews);
        }
    };

    // 3. Handle Category Selection
    const handleCategoryChange = (e: ChangeEvent<HTMLSelectElement>, level: number) => {
        const catId = parseInt(e.target.value);
        const newPath = selectedCategoryPath.slice(0, level);
        if (catId) newPath.push(catId);
        setSelectedCategoryPath(newPath);
    };

    // 4. Submit to Spring Boot
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('sellerToken'); // Token check
        if (!token) {
            setError('Please login as a seller first.');
            setLoading(false);
            return;
        }

        if (selectedCategoryPath.length === 0) {
            setError('Please select a category.');
            setLoading(false);
            return;
        }

        const finalCategoryId = selectedCategoryPath[selectedCategoryPath.length - 1];

        const formData = new FormData();
        formData.append('productName', productName);
        formData.append('description', description);
        formData.append('price', price);
        formData.append('minimumOrderQuantity', minQuantity);
        formData.append('unit', quantityUnit);
        formData.append('hsnSku', hsnSku);
        formData.append('categoryId', finalCategoryId.toString());
        if (youtubeUrl) formData.append('youtubeUrl', youtubeUrl);
        
        // Append Multiple Images
        selectedImages.forEach((file) => {
            formData.append('imageFiles', file); // Note: Key name matches Backend param
        });

        try {
            const response = await fetch(`${SPRING_API_URL}/api/seller/products`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                const txt = await response.text();
                throw new Error(txt);
            }

            setSuccess('Product Added Successfully!');
            setTimeout(() => navigate('/seller/product'), 1500);

        } catch (err: any) {
            setError(err.message || 'Error adding product');
        } finally {
            setLoading(false);
        }
    };

    // Recursive Category Dropdowns
    let currentCats = nestedCategories;
    const dropDowns = [];
    for (let i = 0; i <= selectedCategoryPath.length; i++) {
        if (!currentCats || currentCats.length === 0) break;
        const val = selectedCategoryPath[i] || '';
        
        dropDowns.push(
            <div key={i} className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                    {i === 0 ? 'Main Category' : `Sub Category Level ${i}`}
                </label>
                <select
                    value={val}
                    onChange={(e) => handleCategoryChange(e, i)}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                >
                    <option value="">Select Category</option>
                    {currentCats.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>
        );

        if (val) {
            const selectedCat = currentCats.find(c => c.id === val);
            currentCats = selectedCat?.subCategories || [];
        } else {
            break;
        }
    }

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-10">
            <h1 className="text-2xl font-bold mb-6 text-gray-800">Add New Product</h1>
            
            {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}
            {success && <div className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</div>}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Product Name *</label>
                        <input type="text" required className="mt-1 block w-full border p-2 rounded" 
                            value={productName} onChange={e => setProductName(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Price (INR) *</label>
                        <input type="number" required className="mt-1 block w-full border p-2 rounded" 
                            value={price} onChange={e => setPrice(e.target.value)} />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Description *</label>
                    <textarea required rows={4} className="mt-1 block w-full border p-2 rounded"
                        value={description} onChange={e => setDescription(e.target.value)} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Min Order Qty *</label>
                        <input type="number" required className="mt-1 block w-full border p-2 rounded"
                            value={minQuantity} onChange={e => setMinQuantity(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Unit (e.g. Kg, Pcs) *</label>
                        <input type="text" required className="mt-1 block w-full border p-2 rounded"
                            value={quantityUnit} onChange={e => setQuantityUnit(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">HSN/SKU Code</label>
                        <input type="text" className="mt-1 block w-full border p-2 rounded"
                            value={hsnSku} onChange={e => setHsnSku(e.target.value)} />
                    </div>
                </div>

                <div className="bg-gray-50 p-4 rounded border">
                    <h3 className="text-lg font-medium mb-2">Category Selection</h3>
                    {dropDowns}
                </div>

                {/* --- Multiple Image Upload --- */}
                <div className="border-2 border-dashed p-6 text-center rounded">
                    <input 
                        type="file" 
                        accept="image/*" 
                        multiple // Allow multiple files
                        onChange={handleFileChange} 
                        ref={fileInputRef} 
                    />
                    <p className="text-xs text-gray-500 mt-2">Hold Ctrl to select multiple images</p>
                    
                    <div className="flex flex-wrap gap-4 mt-4 justify-center">
                        {previewUrls.map((url, index) => (
                            <img key={index} src={url} alt={`Preview ${index}`} className="h-24 w-24 object-cover rounded shadow border" />
                        ))}
                    </div>
                </div>

                <button disabled={loading} type="submit" className="w-full bg-blue-600 text-white py-3 rounded font-bold hover:bg-blue-700 transition">
                    {loading ? 'Adding Product...' : 'Submit Product'}
                </button>
            </form>
        </div>
    );
};

export default AddNewProductPage;